import {inject} from "@angular/core";
import type {Routes, UrlTree} from "@angular/router";
import type {IEquatable} from "./helpers/Equatable";
import type {RoutePathParameter, RouteSegments} from "./helpers/RoutingTypes";
import {areEqualRouteSegments} from "./helpers/RoutingTypes";
import {CredentialsStorage} from "./services/credentials-storage";
import {isLoggedIn} from "./services/guards/permission-guard";
import {bactoforceRoutes} from "./sites/bactoforce/bactoforce.routes";
import {calendarRoutes} from "./sites/calendar/calendar.routes";
import {customerRoutes} from "./sites/customers/customer.routes";
import {enterpriseRoutes} from "./sites/enterprise/enterprise.routes";
import {i18nRoutes} from "./sites/i18n/i18n.routes";
import {pricingRoutes} from "./sites/pricing/pricing.routes";
import {reportRoutes} from "./sites/reports/report.routes";
import {riskManagementRoutes} from "./sites/risk-management/risk-management.routes";
import {systemRoutes} from "./sites/system/system.routes";
import {thirdPartyCompaniesRoutes} from "./sites/third-party-companies/third-party-companies.routes";
import {timeEntryRoutes} from "./sites/time-entries/time-entry.routes";
import {userRoutes} from "./sites/users/user.routes";
import {visitRoutes} from "./sites/visits/visit.routes";

export const routes = [
  {
    path: "home",
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    loadComponent: () => import("./sites/home/home").then(m => m.Home),
    title: (): string => $localize`Home`,
  },

  {
    path: "dashboard",
    canActivate: [isLoggedIn],
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    loadComponent: () => import("./sites/dashboard/dashboard").then(m => m.Dashboard),
  },

  ...bactoforceRoutes,
  ...calendarRoutes,
  ...customerRoutes,
  ...enterpriseRoutes,
  ...i18nRoutes,
  ...pricingRoutes,
  ...reportRoutes,
  ...riskManagementRoutes,
  ...systemRoutes,
  ...thirdPartyCompaniesRoutes,
  ...timeEntryRoutes,
  ...userRoutes,
  ...visitRoutes,

  // Root
  {
    path: "",
    pathMatch: "full",
    redirectTo: (): string | UrlTree => {
      const credentials = inject(CredentialsStorage).nonExpiredCredentials();
      if (credentials) {
        if (credentials.needsPasswordChange) return "/change-password" satisfies AppRoute;
        if (credentials.isValid) return "/dashboard" satisfies AppRoute;
      }
      return "/home" satisfies AppRoute;
    },
  },
] as const satisfies Routes;

export type AppRoute<ParamsType extends RoutePathParameter | IEquatable = RoutePathParameter> = RouteSegments<typeof routes, ParamsType>;

export function isEqualAppRoute<P extends RoutePathParameter | IEquatable = RoutePathParameter>(
  a: Readonly<AppRoute<P>>, b: Readonly<AppRoute<P>>,
): boolean {
  return areEqualRouteSegments<typeof routes, P>(a, b);
}

export function resolveAppRoute<RouteParam extends IEquatable>(
  route: Readonly<AppRoute<RoutePathParameter | RouteParam>>,
  paramResolve: (p: RouteParam) => RoutePathParameter,
): AppRoute {
  if (!Array.isArray(route)) return route;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return route.map(r => typeof r === "object" ? paramResolve(r) : r) as AppRoute;
}
